// The dispatch conversation contract (#2676) — ONE shape an external agent can
// implement against, regardless of which conversation product it reaches us
// through. The contract is mode-extensible (so devops slots in later without a
// v2 break) and state-explicit (eight named states, one discriminated union), and
// the resumable wait reuses `watchLongPoll` (`packages/mcp/src/army/task.ts`)
// rather than inventing a second long-poll primitive.
//
// v1 implements the scout and architect ends only; devops is a contract hook,
// not an implementation (#2645 owns that surface). The error taxonomy is part
// of this contract, not a separate concern: auth, preflight-failed,
// needs-provisioning, execution-failed, plus the refusals that already exist
// (`queue_full`, `no_scout_capacity`, `no_capacity`,
// `RepoNotEnabledError`) — every code in that taxonomy maps to a named entry
// in `DispatchContractRefusalCodeSchema` below; none is a bare string or `null`.
//
// The schema is conservative: every field is REQUIRED and every state ships a
// discriminating `state` plus the minimum context the consumer of that state
// needs to act. The `mode` field is closed for v1 — adding a value is an
// additive change that lands alongside its handler rather than mid-flight, the
// same posture `TaskOriginSchema` takes.

import { z } from 'zod';

/**
 * The dispatch MODE this message belongs to. v1 ships six values; adding a
 * seventh is additive and lands alongside its handler.
 *
 * `architect` is the conversational lane that shapes work before code
 * (`@developerz/agent` `role-architect` doctrine — `#2683`); `scout` and
 * `architect` reach the SAME lane, but the contract lists them apart because
 * an external agent that wants "shape the work" should not have to know that
 * the platform merged them.
 */
export const DispatchContractMode = {
  Scout: 'scout',
  Triage: 'triage',
  Worker: 'worker',
  Reviewer: 'reviewer',
  Devops: 'devops',
  Architect: 'architect',
} as const;
export const DispatchContractModeSchema = z.enum(DispatchContractMode);
export type DispatchContractMode = z.infer<typeof DispatchContractModeSchema>;

/**
 * The named refusal codes the contract admits on a `preflight` message. The
 * refusal codes the platform already returns today — `queue_full`
 * (`QueueDepthExceededError`, `packages/agent/src/dispatch/sinks.ts`),
 * `no_scout_capacity` (`packages/agent/src/planner.ts`), `no_capacity`
 * (`packages/mcp/src/tools.ts`) and `repo_not_enabled`
 * (`RepoNotEnabledError`, `packages/agent/src/dispatch/task-dispatcher.ts`) —
 * are typed here, alongside the four the contract surfaces of its own
 * (`auth_required`, `preflight_failed`, `needs_provisioning`, `ready`). Every
 * refusal maps to a named entry; none is a bare string or a `null`.
 *
 * `ready` is the positive outcome a `preflight` message carries alongside
 * `ok: true` — the gate cleared and the lane can take the work. `ok: false`
 * carries ONE of the seven refusal codes; the schema's `superRefine` rejects a
 * mismatch so a `{ok:false, verdict:'ready'}` is the bug it reads as.
 */
export const DispatchContractRefusalCode = {
  AuthRequired: 'auth_required',
  PreflightFailed: 'preflight_failed',
  NeedsProvisioning: 'needs_provisioning',
  QueueFull: 'queue_full',
  NoScoutCapacity: 'no_scout_capacity',
  NoCapacity: 'no_capacity',
  RepoNotEnabled: 'repo_not_enabled',
  Ready: 'ready',
} as const;
export const DispatchContractRefusalCodeSchema = z.enum(DispatchContractRefusalCode);
export type DispatchContractRefusalCode = z.infer<typeof DispatchContractRefusalCodeSchema>;

/**
 * The eight states of the conversation. A message is a discriminated union
 * over `state`; each arm carries the minimum context its consumer needs to act.
 *
 *   negotiate    — a peer proposes a session: account, repo, prompt, the
 *                  conversation ref to stamp on the resulting task.
 *   preflight    — the peer's response: whether the lane can take the work
 *                  (capacity + repo state), and what it would need to do so.
 *   plan         — a scout emits its plan: groups, dependencies, the conversation
 *                  ref the plan's tasks should inherit.
 *   plan_review  — the maintainer's verdict on a plan; flips held → dispatched.
 *   approve      — a maintainer approves a held plan (mirrors the MCP `plan
 *                  approve` action, but at the conversation contract).
 *   execute      — a coding/devops run's start signal: dispatch summary, the
 *                  task id, the lane that took it.
 *   status       — a peer's reply during execution: progress, decisions,
 *                  evidence (files touched, findings, the same anchors the
 *                  reviewer posts).
 *   rollup       — the final summary: the merged/PR's outcome, the conversation
 *                  ref, the agent's last assistant text.
 */
export const DispatchContractState = {
  Negotiate: 'negotiate',
  Preflight: 'preflight',
  Plan: 'plan',
  PlanReview: 'plan_review',
  Approve: 'approve',
  Execute: 'execute',
  Status: 'status',
  Rollup: 'rollup',
} as const;
export const DispatchContractStateSchema = z.enum(DispatchContractState);
export type DispatchContractState = z.infer<typeof DispatchContractStateSchema>;

/**
 * The conversation this message belongs to (#1896). The contract is the
 * conversation ref's wire — the platform stamps it onto every task the
 * conversation produces, every run event it emits, every PR body it writes —
 * and the conversation ref is what makes that all reachable from a single
 * caller's session id.
 *
 * Caller-supplied (never server-derived). Bounded charset (`[A-Za-z0-9_-]`,
 * 1..200 chars) and the same one every envelope-side field uses, so a
 * conversation ref cannot smuggle markdown or a URL into a PR body.
 */
const conversationRefField = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[A-Za-z0-9_-]+$/, 'conversation_ref must be 1-200 chars of [A-Za-z0-9_-]');

/**
 * The mode-extensible body. Required on every message so a router that does
 * not know a mode REJECTS rather than guesses. The schema is closed for v1;
 * the test that asserts a `Record<DispatchContractMode, ...>` enumerates the
 * values and fails the build when a value is added without a writer.
 */
const modeField = DispatchContractModeSchema;

/**
 * The state-discriminated message. Each arm is the MINIMUM the consumer of
 * that state needs to act. A `status` message carries the progress text, not
 * the run events; the consumer that wants the events reads them out of
 * `fleet.evt.<run_id>` itself.
 *
 * Fields are REQUIRED (the discriminator is one of them) so a missing-key
 * failure surfaces at the schema boundary, never as a hand-rolled "you forgot
 * X" message halfway through the consumer.
 */
export const DispatchContractMessageSchema = z.discriminatedUnion('state', [
  z.object({
    state: z.literal(DispatchContractState.Negotiate),
    mode: modeField,
    account_id: z.string().min(1),
    repo_full_name: z.string().min(1).max(200),
    prompt: z.string().min(1).max(16_000),
    conversation_ref: conversationRefField,
  }),
  z
    .object({
      state: z.literal(DispatchContractState.Preflight),
      mode: modeField,
      conversation_ref: conversationRefField,
      ok: z.boolean(),
      /**
       * The named, typed verdict. `ok: true` carries `ready`; `ok: false` carries
       * one of the seven refusal codes. The coupling is asserted by the
       * `superRefine` below — a `{ok: true, verdict: 'queue_full'}` (or the
       * inverse) is the kind of silent disagreement the schema exists to reject.
       */
      verdict: DispatchContractRefusalCodeSchema,
    })
    .superRefine((m, ctx) => {
      if (m.ok && m.verdict !== DispatchContractRefusalCode.Ready) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `ok=true preflight must carry verdict='${DispatchContractRefusalCode.Ready}', got '${m.verdict}'`,
          path: ['verdict'],
        });
      }
      if (!m.ok && m.verdict === DispatchContractRefusalCode.Ready) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `ok=false preflight must carry a refusal code, got '${DispatchContractRefusalCode.Ready}'`,
          path: ['verdict'],
        });
      }
    }),
  z.object({
    state: z.literal(DispatchContractState.Plan),
    mode: modeField,
    conversation_ref: conversationRefField,
    groups: z
      .array(
        z.object({
          group_id: z.string().min(1),
          title: z.string().min(1).max(200),
          depends_on: z.array(z.string()).default([]),
        }),
      )
      .min(1),
  }),
  z.object({
    state: z.literal(DispatchContractState.PlanReview),
    mode: modeField,
    conversation_ref: conversationRefField,
    plan_id: z.string().min(1).max(200),
    verdict: z.enum(['approve', 'request_changes']),
    note: z.string().min(1).max(2000).optional(),
  }),
  z.object({
    state: z.literal(DispatchContractState.Approve),
    mode: modeField,
    conversation_ref: conversationRefField,
    plan_id: z.string().min(1).max(200),
    approved: z.boolean(),
    approved_groups: z
      .array(
        z.object({
          group_id: z.string().min(1),
          task_id: z.string().min(1),
        }),
      )
      .default([]),
  }),
  z.object({
    state: z.literal(DispatchContractState.Execute),
    mode: modeField,
    conversation_ref: conversationRefField,
    task_id: z.string().min(1),
    lane: z.string().min(1),
  }),
  z.object({
    state: z.literal(DispatchContractState.Status),
    mode: modeField,
    conversation_ref: conversationRefField,
    progress: z.string().min(1).max(8000),
  }),
  z.object({
    state: z.literal(DispatchContractState.Rollup),
    mode: modeField,
    conversation_ref: conversationRefField,
    outcome: z.enum(['merged', 'closed', 'failed', 'cancelled', 'needs_attention']),
    summary: z.string().min(1).max(8000),
  }),
]);
export type DispatchContractMessage = z.infer<typeof DispatchContractMessageSchema>;

/**
 * One example message per state, all eight present. The shape is OPEN
 * (caller's choice: array or record) — the live test reads coverage from
 * each example's own `state` rather than from container keys, so neither
 * shape can fail a correct implementation. A test asserting all eight states
 * are represented lives in `dispatch-contract.test.ts` and a 9th state would
 * be a build error (the schema's `z.discriminatedUnion`).
 */
export const CONTRACT_MESSAGE_EXAMPLES: ReadonlyArray<DispatchContractMessage> = [
  {
    state: DispatchContractState.Negotiate,
    mode: DispatchContractMode.Scout,
    account_id: 'acc_dev_1',
    repo_full_name: 'developerz-ai/wurk',
    prompt: 'What is the highest-leverage cleanup we can make this week?',
    conversation_ref: 'cnv_thread_42',
  },
  {
    state: DispatchContractState.Preflight,
    mode: DispatchContractMode.Scout,
    conversation_ref: 'cnv_thread_42',
    ok: true,
    verdict: DispatchContractRefusalCode.Ready,
  },
  {
    state: DispatchContractState.Plan,
    mode: DispatchContractMode.Scout,
    conversation_ref: 'cnv_thread_42',
    groups: [
      { group_id: 'g1', title: 'Slim the dev login', depends_on: [] },
      { group_id: 'g2', title: 'Cover the gate with a contract test', depends_on: ['g1'] },
    ],
  },
  {
    state: DispatchContractState.PlanReview,
    mode: DispatchContractMode.Scout,
    conversation_ref: 'cnv_thread_42',
    plan_id: 'plan_run_1ebfa4bd',
    verdict: 'approve',
  },
  {
    state: DispatchContractState.Approve,
    mode: DispatchContractMode.Architect,
    conversation_ref: 'cnv_thread_42',
    plan_id: 'plan_run_1ebfa4bd',
    approved: true,
    approved_groups: [{ group_id: 'g1', task_id: 'tsk_abc' }],
  },
  {
    state: DispatchContractState.Execute,
    mode: DispatchContractMode.Worker,
    conversation_ref: 'cnv_thread_42',
    task_id: 'tsk_abc',
    lane: 'worker',
  },
  {
    state: DispatchContractState.Status,
    mode: DispatchContractMode.Worker,
    conversation_ref: 'cnv_thread_42',
    progress: 'task_started: reading the gate at apps/api/src/middleware/auth.ts',
  },
  {
    state: DispatchContractState.Rollup,
    mode: DispatchContractMode.Worker,
    conversation_ref: 'cnv_thread_42',
    outcome: 'merged',
    summary: 'PR merged at #1982; the gate now lives at line 247.',
  },
];
